import React, { useState } from 'react';
import {
  FiSearch,
  FiMoreVertical,
  FiLoader,
} from 'react-icons/fi';

import { TopBar } from '@/design-system/components/organisms/TopBar';
import {
  AddToBlocklistModal,
  type AddToBlocklistData,
} from '@/features/onboarding/components/AddToBlocklistModal';
import { DataTable, type DataTableColumn } from '@/features/onboarding/components/DataTable';
import {
  RemoveFromBlocklistModal,
  type RemoveFromBlocklistData,
} from '@/features/onboarding/components/RemoveFromBlocklistModal';
import { Sidebar } from '@/features/onboarding/components/Sidebar';
import { SuccessModal } from '@/features/onboarding/components/SuccessModal';
import {
  useBlocklist,
  useCreateBlocklist,
  useDeactivateBlocklist,
} from '@/features/onboarding/hooks/useBlocklist';
import { TAB_TO_PROPERTY, type BlocklistProperty, type BlocklistItem } from '@/features/onboarding/types';
import { ONBOARDING_SIDEBAR_ITEMS } from '@/shared/constants/sidebar.constants';
import { formatByProperty, formatCPF, formatCNPJ, formatPhone } from '@/shared/utils/formatters';

type BlocklistTab = 'CNPJ' | 'CPF' | 'Email' | 'Celular';

export const BlocklistPage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [activeTab, setActiveTab] = useState<BlocklistTab>('CNPJ');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(15);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<{ id: string; value: string } | null>(null);
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);
  const [addError, setAddError] = useState<string | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState({ title: '', message: '' });

  // Map tab to API property
  const activeProperty: BlocklistProperty = TAB_TO_PROPERTY[activeTab];

  // Fetch blocklist data
  const { data, isLoading, isError, error } = useBlocklist({
    page: currentPage,
    limit: itemsPerPage,
    search: debouncedSearch || undefined,
    property: activeProperty,
    isActive: true,
  });

  const blocklistItems = data?.data ?? [];
  const pagination = data?.pagination;
  const totalItems = pagination?.totalItems ?? 0;
  const totalPages = pagination?.totalPages ?? 1;

  // Create blocklist mutation
  const createBlocklistMutation = useCreateBlocklist();

  // Deactivate blocklist mutation
  const deactivateBlocklistMutation = useDeactivateBlocklist();

  // Apply mask based on active tab
  const applySearchMask = (value: string): string => {
    switch (activeTab) {
      case 'CPF':
        return formatCPF(value);
      case 'CNPJ':
        return formatCNPJ(value);
      case 'Celular':
        return formatPhone(value);
      case 'Email':
      default:
        return value;
    }
  };

  // Remove mask to send raw value to API
  const removeSearchMask = (value: string): string => {
    if (activeTab === 'Email') return value;
    return value.replace(/\D/g, '');
  };

  // Debounce search
  const handleSearchChange = (value: string) => {
    const maskedValue = applySearchMask(value);
    setSearchQuery(maskedValue);
    setCurrentPage(1);

    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    const timeout = setTimeout(() => {
      // Send raw value (without mask) to API
      const rawValue = removeSearchMask(maskedValue);
      setDebouncedSearch(rawValue);
    }, 500);

    setSearchTimeout(timeout);
  };

  const handleTabChange = (tab: BlocklistTab) => {
    setActiveTab(tab);
    setCurrentPage(1);
    setSearchQuery('');
    setDebouncedSearch('');
  };

  const handleAddToBlocklist = (formData: AddToBlocklistData) => {
    setAddError(null);
    createBlocklistMutation.mutate(formData, {
      onSuccess: () => {
        setIsModalOpen(false);
        setSuccessMessage({
          title: 'Adicionado com sucesso!',
          message: 'O item foi adicionado à blacklist com sucesso.',
        });
        setIsSuccessModalOpen(true);
      },
      onError: (err) => {
        // Handle API error messages
        const errorMessage = err instanceof Error ? err.message : 'Erro ao adicionar à blacklist';
        setAddError(errorMessage);
      },
    });
  };

  const handleRemoveFromBlocklist = (formData: RemoveFromBlocklistData) => {
    if (!selectedItem) return;

    setRemoveError(null);
    deactivateBlocklistMutation.mutate(
      { id: selectedItem.id, reason: formData.reason },
      {
        onSuccess: () => {
          setIsRemoveModalOpen(false);
          setSelectedItem(null);
          setSuccessMessage({
            title: 'Removido com sucesso!',
            message: 'O item foi removido da blacklist com sucesso.',
          });
          setIsSuccessModalOpen(true);
        },
        onError: (err) => {
          const errorMessage = err instanceof Error ? err.message : 'Erro ao remover da blacklist';
          setRemoveError(errorMessage);
        },
      }
    );
  };

  const handleOpenRemoveModal = (item: { id: string; value: string }) => {
    setSelectedItem(item);
    setIsRemoveModalOpen(true);
  };

  const tabs: BlocklistTab[] = ['CNPJ', 'CPF', 'Email', 'Celular'];

  const getPlaceholder = () => {
    switch (activeTab) {
      case 'CNPJ':
        return 'Buscar por CNPJ';
      case 'CPF':
        return 'Buscar por CPF';
      case 'Email':
        return 'Buscar por Email';
      case 'Celular':
        return 'Buscar por Celular';
      default:
        return 'Buscar';
    }
  };

  // Columns for DataTable
  const columns: DataTableColumn<BlocklistItem>[] = [
    {
      key: 'valueProperty',
      label: 'Campo bloqueado',
      width: '90%',
      render: (value: unknown) => (
        <span className="text-sm text-neutral-700">
          {formatByProperty(value as string, activeProperty)}
        </span>
      ),
    },
    {
      key: 'actions',
      label: 'Ações',
      width: '10%',
      render: (_: unknown, row: BlocklistItem) => (
        <button
          type="button"
          onClick={() => {
            handleOpenRemoveModal({ id: row.id, value: row.valueProperty });
          }}
          className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
        >
          <FiMoreVertical className="w-5 h-5" />
        </button>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-neutral-50">
      <TopBar />

      <div className="flex">
        {/* Sidebar */}
        <Sidebar items={ONBOARDING_SIDEBAR_ITEMS} className="w-64 min-h-[calc(100vh-140px)]" />

        {/* Main Content */}
        <main className="flex-1 p-8">
          <div className="max-w-7xl mx-auto">
            {/* Search Bar */}
            <div className="mb-6">
              <div className="relative">
                <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
                <input
                  type="text"
                  placeholder={getPlaceholder()}
                  value={searchQuery}
                  onChange={(e) => {
                    handleSearchChange(e.target.value);
                  }}
                  className="w-full py-3 pl-12 pr-4 bg-white border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                />
              </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-6 mb-6 border-b border-neutral-200">
              {tabs.map((tab) => (
                <button
                  key={tab}
                  type="button"
                  onClick={() => {
                    handleTabChange(tab);
                  }}
                  className={`pb-3 text-sm font-medium transition-colors relative ${
                    activeTab === tab
                      ? 'text-neutral-800'
                      : 'text-neutral-500 hover:text-neutral-700'
                  }`}
                >
                  {tab}
                  {activeTab === tab && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-neutral-800" />
                  )}
                </button>
              ))}
            </div>

            {/* Loading State */}
            {isLoading && (
              <div className="bg-white rounded-lg border border-neutral-200 p-6 min-h-[500px] flex items-center justify-center">
                <FiLoader className="w-8 h-8 text-purple-600 animate-spin" />
                <span className="ml-3 text-neutral-600">Carregando...</span>
              </div>
            )}

            {/* Error State */}
            {isError && (
              <div className="bg-white rounded-lg border border-neutral-200 p-6 min-h-[500px] flex items-center justify-center">
                <div className="text-center">
                  <p className="text-red-600 font-medium">Erro ao carregar dados</p>
                  <p className="text-sm text-neutral-500 mt-1">
                    {error instanceof Error ? error.message : 'Tente novamente mais tarde'}
                  </p>
                </div>
              </div>
            )}

            {/* Empty State */}
            {!isLoading && !isError && blocklistItems.length === 0 && (
              <div className="bg-white rounded-lg border border-neutral-200 p-6 min-h-[500px] flex items-center justify-center">
                <div className="text-center">
                  <p className="text-neutral-600 font-medium">Nenhum item encontrado</p>
                  <p className="text-sm text-neutral-500 mt-1">
                    {debouncedSearch
                      ? `Nenhum resultado para "${debouncedSearch}"`
                      : `Não há itens na blacklist de ${activeTab}`}
                  </p>
                </div>
              </div>
            )}

            {/* Data Table */}
            {!isLoading && !isError && blocklistItems.length > 0 && (
              <DataTable
                columns={columns}
                data={blocklistItems}
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
                onPageChange={setCurrentPage}
                onItemsPerPageChange={setItemsPerPage}
              />
            )}

            {/* Add Button - Outside table */}
            <div className="flex justify-end mt-6">
              <button
                type="button"
                onClick={() => {
                  setIsModalOpen(true);
                }}
                className="px-6 py-3 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors"
              >
                Adicionar a Blacklist
              </button>
            </div>
          </div>
        </main>
      </div>

      {/* Add to Blocklist Modal */}
      <AddToBlocklistModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setAddError(null);
        }}
        onConfirm={handleAddToBlocklist}
        isLoading={createBlocklistMutation.isPending}
        error={addError}
      />

      {/* Remove from Blocklist Modal */}
      <RemoveFromBlocklistModal
        isOpen={isRemoveModalOpen}
        onClose={() => {
          setIsRemoveModalOpen(false);
          setSelectedItem(null);
          setRemoveError(null);
        }}
        onConfirm={handleRemoveFromBlocklist}
        isLoading={deactivateBlocklistMutation.isPending}
        error={removeError}
        itemValue={selectedItem?.value}
      />

      {/* Success Modal */}
      <SuccessModal
        isOpen={isSuccessModalOpen}
        onClose={() => {
          setIsSuccessModalOpen(false);
        }}
        title={successMessage.title}
        message={successMessage.message}
        buttonText="OK"
      />
    </div>
  );
};
